/* eslint-disable import/prefer-default-export */
import { types as sdkTypes } from './sdkLoader';
import { formatMoney } from './currency';
import { formatList } from './array';
import {
  houseAmenitiesOptions,
  houseCookingOptions,
  cabinAmenitiesOptions,
  cabinCookingOptions,
  rvParkingOptions,
  rvElectricHookupOptions,
  rvSewageHookupOptions,
  garbageAreaOptions,
  rvRoadQualityOptions,
  rvAccessOptions,
  rvTypesOptions,
  tentCookingAreaOptions,
  rvAmenitiesOptions,
} from '../marketplace-custom-config';

const getLabels = (key, options) => {
  const option = options.find(item => item.key === key);
  return option ? option.label : null;
};

export const getLodgingIcon = type => {
  const iconMap = {
    house: 'Lodge',
    cabin: 'Cabin',
    tent: 'Tent',
    rv: 'Rv',
  };
  return iconMap[type] || 'Lodge';
};

export const collectAllAmenities = (lodgingData, intl, lodgingType = 'house') => {
  const allAmenities = [];

  // Select the appropriate options based on lodging type
  const amenitiesOptions = lodgingType === 'cabin' ? cabinAmenitiesOptions : houseAmenitiesOptions;
  const cookingOptions = lodgingType === 'cabin' ? cabinCookingOptions : houseCookingOptions;

  // Basic amenities
  if (lodgingData.amenities && lodgingData.amenities.length > 0) {
    lodgingData.amenities.forEach(item => {
      const label = getLabels(item, amenitiesOptions);
      if (label) allAmenities.push(label);
    });
  }

  // Cooking amenities
  if (lodgingData.cooking && lodgingData.cooking.length > 0) {
    lodgingData.cooking.forEach(item => {
      const label = getLabels(item, cookingOptions);
      if (label) allAmenities.push(label);
    });
  }

  // WiFi
  if (lodgingData.hasWifi) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesWifi' }));
  }

  // Pets
  if (lodgingData.allowsPets) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesPets' }));
  }

  // Electricity
  if (lodgingData.hasElectricity) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesElectricity' }));
  }

  // Garbage Area
  if (lodgingData.hasGarbage) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesGarbageArea' }));
  }

  // Drinking Water
  if (lodgingData.hasDrinkingWater) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesDrinkingWater' }));
  }

  // Water Hookup
  if (lodgingData.hasWaterHookup) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesWaterHookup' }));
  }

  // Generators
  if (lodgingData.allowsGenerators) {
    allAmenities.push(intl.formatMessage({ id: 'ListingPage.amenitiesGeneratorsAllowed' }));
  }

  return allAmenities;
};

export const buildCabinFeatures = (cabin, lodging, cabinOption, intl, isOptional) => {
  if (!cabin || cabin.sportsmen === 0) return null;

  const items = [];

  // Add guests
  items.push({
    iconVariant: 'UserSolid',
    label: intl.formatMessage({ id: 'ListingCard.Guests' }, { count: cabin.sportsmen }),
  });

  // Add bedrooms info
  if (cabin.bedrooms > 0) {
    const bedsArray = [];
    if (cabin.bunkBeds > 0) bedsArray.push(`${cabin.bunkBeds} Bunk`);
    if (cabin.queenBeds > 0) bedsArray.push(`${cabin.queenBeds} Queen`);
    if (cabin.kingBeds > 0) bedsArray.push(`${cabin.kingBeds} King`);
    if (cabin.singleBeds > 0) bedsArray.push(`${cabin.singleBeds} Single`);
    if (cabin.doubleBeds > 0) bedsArray.push(`${cabin.doubleBeds} Double`);

    const bedsInfo = bedsArray.length > 0 ? bedsArray.join(', ') : undefined;
    items.push({
      iconVariant: 'BedSolid',
      label: intl.formatMessage(
        { id: 'ListingPage.cabinBedroomsTitle' },
        { count: cabin.bedrooms }
      ),
      subtitle: bedsInfo,
    });
  }

  // Add bathrooms info
  if (cabin.bathrooms > 0) {
    const bathroomFeatures = [];
    if (cabin.hasHotWater) {
      bathroomFeatures.push(intl.formatMessage({ id: 'ListingPage.hasHotWater' }));
    }
    if (cabin.hasShower) {
      bathroomFeatures.push(intl.formatMessage({ id: 'ListingPage.hasShower' }));
    }

    items.push({
      iconVariant: 'BathSolid',
      label: intl.formatMessage({ id: 'ListingPage.bathrooms' }, { count: cabin.bathrooms }),
      subtitle: bathroomFeatures.length > 0 ? bathroomFeatures.join(', ') : undefined,
    });
  }

  // Collect all amenities into a comma-separated list
  const allAmenities = collectAllAmenities(cabin, intl, 'cabin');

  // Add all amenities as a single item if there are any
  if (allAmenities.length > 0) {
    items.push({
      iconVariant: 'Amenitity',
      label: intl.formatMessage({ id: 'ListingPage.houseAmenities' }),
      subtitle: allAmenities.join(', '),
    });
  }

  // Add overall description if available
  if (lodging?.overallInfo && lodging.overallInfo.length > 0) {
    items.push({
      iconVariant: 'InfoCircle',
      label: intl.formatMessage({ id: 'ListingPage.houseDescription' }),
      subtitle: lodging.overallInfo,
    });
  }

  const cabinLabel = intl.formatMessage({ id: 'ListingPage.lodgingCabinSite' });

  if (isOptional) {
    // For optional, return as a section with heading and price
    let priceInfo = '';
    if (cabinOption?.price) {
      const parsedPrice = JSON.parse(cabinOption.price, sdkTypes.reviver);
      priceInfo = formatMoney(intl, parsedPrice);
    } else if (lodging?.price) {
      const parsedPrice = JSON.parse(lodging.price, sdkTypes.reviver);
      priceInfo = formatMoney(intl, parsedPrice);
    }

    return {
      heading: `${cabinLabel}${priceInfo ? ` • ${priceInfo}` : ''} ${intl.formatMessage({
        id: 'ListingPage.lodgingPriceFieldPostfix',
      })}`,
      items,
    };
  }

  // For required, return heading and items with "Sleeps" as first item
  return {
    heading: `${cabinLabel} ${intl.formatMessage({ id: 'ListingPage.lodgingAccessTitle' })}`,
    items,
  };
};

export const buildHouseFeatures = (house, lodging, houseOption, intl, isOptional) => {
  if (!house || house.sportsmen === 0) return null;

  const items = [];

  // Add guests
  items.push({
    iconVariant: 'UserSolid',
    label: intl.formatMessage({ id: 'ListingCard.Guests' }, { count: house.sportsmen }),
  });

  // Add bedrooms info
  if (house.bedrooms > 0) {
    const bedsArray = [];
    if (house.bunkBeds > 0) bedsArray.push(`${house.bunkBeds} Bunk`);
    if (house.queenBeds > 0) bedsArray.push(`${house.queenBeds} Queen`);
    if (house.kingBeds > 0) bedsArray.push(`${house.kingBeds} King`);
    if (house.singleBeds > 0) bedsArray.push(`${house.singleBeds} Single`);
    if (house.doubleBeds > 0) bedsArray.push(`${house.doubleBeds} Double`);

    const bedsInfo = bedsArray.length > 0 ? bedsArray.join(', ') : undefined;
    items.push({
      iconVariant: 'BedSolid',
      label: intl.formatMessage(
        { id: 'ListingPage.cabinBedroomsTitle' },
        { count: house.bedrooms }
      ),
      subtitle: bedsInfo,
    });
  }

  // Add bathrooms info
  if (house.bathrooms > 0) {
    const bathroomFeatures = [];
    if (house.hasHotWater) {
      bathroomFeatures.push(intl.formatMessage({ id: 'ListingPage.hasHotWater' }));
    }
    if (house.hasShower) {
      bathroomFeatures.push(intl.formatMessage({ id: 'ListingPage.hasShower' }));
    }

    items.push({
      iconVariant: 'BathSolid',
      label: intl.formatMessage({ id: 'ListingPage.bathrooms' }, { count: house.bathrooms }),
      subtitle: bathroomFeatures.length > 0 ? bathroomFeatures.join(', ') : undefined,
    });
  }

  // Collect all amenities into a comma-separated list
  const allAmenities = collectAllAmenities(house, intl, 'house');

  // Add all amenities as a single item if there are any
  if (allAmenities.length > 0) {
    items.push({
      iconVariant: 'Amenitity',
      label: intl.formatMessage({ id: 'ListingPage.houseAmenities' }),
      subtitle: allAmenities.join(', '),
    });
  }

  // Add overall description if available
  if (lodging?.overallInfo && lodging.overallInfo.length > 0) {
    items.push({
      iconVariant: 'InfoCircle',
      label: intl.formatMessage({ id: 'ListingPage.houseDescription' }),
      subtitle: lodging.overallInfo,
    });
  }

  const houseLabel = intl.formatMessage({ id: 'ListingPage.lodgingHouseSite' });

  if (isOptional) {
    // For optional, return as a section with heading and price
    let priceInfo = '';
    if (houseOption?.price) {
      const parsedPrice = JSON.parse(houseOption.price, sdkTypes.reviver);
      priceInfo = formatMoney(intl, parsedPrice);
    } else if (lodging?.price) {
      const parsedPrice = JSON.parse(lodging.price, sdkTypes.reviver);
      priceInfo = formatMoney(intl, parsedPrice);
    }

    return {
      heading: `${houseLabel}${priceInfo ? ` • ${priceInfo}` : ''} ${intl.formatMessage({
        id: 'ListingPage.lodgingPriceFieldPostfix',
      })}`,
      items,
    };
  }

  // For required, return heading and items with "Sleeps" as first item
  return {
    heading: `${houseLabel} ${intl.formatMessage({ id: 'ListingPage.lodgingAccessTitle' })}`,
    items,
  };
};

export const buildTentFeatures = (campsite, tentOption, intl, isOptional) => {
  if (!campsite?.tent) return null;

  const tentLabel = intl.formatMessage({ id: 'ListingPage.lodgingTentSite' });
  const items = [];

  // Tent access - drinking water
  if (campsite.tent.hasDrinkingWater) {
    items.push({
      iconVariant: 'WaterTap',
      label: intl.formatMessage({ id: 'ListingPage.tentWaterAccessTitle' }, { option: 'Yes' }),
    });
  }

  // Additional tent details
  if (campsite.tent.info && campsite.tent.info.length > 0) {
    items.push({
      iconVariant: 'InfoCircle',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsAdditionalTitle' }),
      subtitle: campsite.tent.info,
    });
  }

  if (isOptional) {
    // For optional, return as a section with heading and price
    let tentPrice = '';
    if (tentOption?.price) {
      const parsedPrice = JSON.parse(tentOption.price, sdkTypes.reviver);
      tentPrice = formatMoney(intl, parsedPrice);
    }

    return {
      heading: `${tentLabel}${tentPrice ? ` • ${tentPrice}` : ''} ${intl.formatMessage({
        id: 'ListingPage.lodgingPriceFieldPostfix',
      })}`,
      items: items.length > 0 ? items : [{ iconVariant: 'Tent', label: tentLabel }],
    };
  }

  // For required, return heading with "Tent Access" and items
  return {
    heading: tentLabel,
    items: items.length > 0 ? items : [{ iconVariant: 'Tent', label: tentLabel }],
  };
};

export const buildRVFeatures = (campsite, rvOption, intl, isOptional, rvIndex, hasSingleSite) => {
  if (!campsite?.rv) return null;

  let siteLabel = intl.formatMessage({ id: 'ListingPage.lodgingRVSite' });

  if (!hasSingleSite) {
    siteLabel = `${siteLabel} #${rvIndex}`;
  }

  if (campsite.rv.campsiteName) {
    siteLabel = `${siteLabel} ${campsite.rv.campsiteName}`;
  }

  const items = [];

  // Parking surface
  if (campsite.rv.parking?.length > 0) {
    const parkingLabels = formatList(
      campsite.rv.parking.map(item => getLabels(item, rvParkingOptions))
    );
    items.push({
      iconVariant: 'SquareParking',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsParkingTitle' },
        { option: parkingLabels }
      ),
    });
  }

  // Surface leveled
  if (campsite.rv.isSurfaceLevel) {
    items.push({
      iconVariant: 'Level',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsSurfaceLeveledTitle' },
        { option: 'Yes' }
      ),
      subtitle: campsite.rv.surfaceInfo || undefined,
    });
  }

  // Road Quality
  if (campsite.rv.roadQuality?.length > 0) {
    const roadQualityLabels = formatList(
      campsite.rv.roadQuality.map(item => getLabels(item, rvRoadQualityOptions))
    );
    const roadInfo = `${roadQualityLabels}. ${
      campsite.rv.is4WDRequired
        ? intl.formatMessage({ id: 'ListingPage.rvDetails4WDRequired' })
        : intl.formatMessage({ id: 'ListingPage.rvDetails4WDNotRequired' })
    }`;
    items.push({
      iconVariant: 'Road',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsRoadQualityTitle' },
        { option: roadInfo }
      ),
    });
  }

  // RV Slab Surface
  if (campsite.rv.surfaceSizeWidth > 0 && campsite.rv.surfaceSizeLength > 0) {
    items.push({
      iconVariant: 'RulerTriangle',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsSlubSurfaceSize' },
        {
          width: campsite.rv.surfaceSizeWidth,
          length: campsite.rv.surfaceSizeLength,
        }
      ),
    });
  }

  // RVs Allowed / Vehicle Types
  if (campsite.rv.vehicleTypes?.length > 0) {
    const typesLabels = formatList(
      campsite.rv.vehicleTypes.map(item => getLabels(item, rvTypesOptions))
    );
    items.push({
      iconVariant: 'Rv',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsTypes' }, { option: typesLabels }),
    });
  }

  // Max length
  if (campsite.rv.maxLength > 0) {
    items.push({
      iconVariant: 'ArrowsLeftRight',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsVehicleLength' },
        { length: campsite.rv.maxLength }
      ),
    });
  }

  // Site Access
  if (campsite.rv.access?.length > 0) {
    const accessLabels = formatList(
      campsite.rv.access.map(item => getLabels(item, rvAccessOptions))
    );
    items.push({
      iconVariant: 'ArrowsLeftRight',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsSiteAccessed' },
        { option: accessLabels }
      ),
    });
  }

  // Electric Hookup
  if (campsite.rv.hasElectricHookup) {
    const electricLabels =
      campsite.rv.electricHookup?.length > 0
        ? formatList(
            campsite.rv.electricHookup.map(item => getLabels(item, rvElectricHookupOptions))
          )
        : 'Yes';
    items.push({
      iconVariant: 'Plug',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsElectricHookupTitle' },
        { option: electricLabels }
      ),
      subtitle: campsite.rv.electricHookupInfo || undefined,
    });
  }

  // Water Hookup
  if (campsite.rv.hasWaterHookup) {
    items.push({
      iconVariant: 'WaterTap',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsWaterHookupTitle' }, { option: 'Yes' }),
      subtitle: campsite.rv.waterHookupInfo || undefined,
    });
  }

  // Sewage Hookup
  if (campsite.rv.hasSewageHookup) {
    const sewageLabels =
      campsite.rv.sewageHookup?.length > 0
        ? formatList(campsite.rv.sewageHookup.map(item => getLabels(item, rvSewageHookupOptions)))
        : undefined;
    items.push({
      iconVariant: 'Sewage',
      label: intl.formatMessage(
        { id: 'ListingPage.rvDetailsSewageHookupTitle' },
        { option: 'Yes' }
      ),
      subtitle:
        (sewageLabels ? `${sewageLabels}` : '') +
          (campsite.rv.sewageHookupInfo ? ` ${campsite.rv.sewageHookupInfo}` : '') || undefined,
    });
  }

  // Generators Allowed
  if (campsite.rv.allowsGenerators) {
    items.push({
      iconVariant: 'Generator',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsGeneratorsTitle' }, { option: 'Yes' }),
      subtitle: campsite.rv.generatorsInfo || undefined,
    });
  }

  // Garbage Area
  if (campsite.rv.hasGarbage) {
    const garbageLabels =
      campsite.rv.garbage?.length > 0
        ? formatList(campsite.rv.garbage.map(item => getLabels(item, garbageAreaOptions)))
        : undefined;
    items.push({
      iconVariant: 'Trash',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsGarbageTitle' }, { option: 'Yes' }),
      subtitle:
        (garbageLabels ? `${garbageLabels}` : '') +
          (campsite.rv.garbageInfo ? ` ${campsite.rv.garbageInfo}` : '') || undefined,
    });
  }

  // Cooking Area
  if (campsite.rv.hasCookingArea) {
    const cookingAreaLabels =
      campsite.rv.cookingArea?.length > 0
        ? formatList(campsite.rv.cookingArea.map(item => getLabels(item, tentCookingAreaOptions)))
        : undefined;
    items.push({
      iconVariant: 'Cooking',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsCookingArea' }, { option: 'Yes' }),
      subtitle: cookingAreaLabels || undefined,
    });
  }

  // Campfires Allowed
  if (campsite.rv.areCampfiresAllowed) {
    items.push({
      iconVariant: 'Campfire',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsCampfiresAllowed' }, { option: 'Yes' }),
    });
  }

  // RV Amenities
  if (campsite.rv.amenities?.length > 0) {
    const amenitiesList = campsite.rv.amenities
      .map(item => getLabels(item, rvAmenitiesOptions))
      .filter(Boolean)
      .join(', ');
    if (amenitiesList) {
      items.push({
        iconVariant: 'TablePicnic',
        label: intl.formatMessage({ id: 'ListingPage.rvDetailsSiteAmenities' }),
        subtitle: amenitiesList,
      });
    }
  }

  // Additional RV details
  if (campsite.rv.info && campsite.rv.info.length > 0) {
    items.push({
      iconVariant: 'InfoCircle',
      label: intl.formatMessage({ id: 'ListingPage.rvDetailsAdditionalTitle' }),
      subtitle: campsite.rv.info,
    });
  }

  if (isOptional) {
    // For optional, return as a section with heading and price
    let rvPrice = '';
    if (rvOption?.price) {
      const parsedPrice = JSON.parse(rvOption.price, sdkTypes.reviver);
      rvPrice = formatMoney(intl, parsedPrice);
    }

    return {
      heading: `${siteLabel}${rvPrice ? ` • ${rvPrice}` : ''} ${intl.formatMessage({
        id: 'ListingPage.lodgingPriceFieldPostfix',
      })}`,
      items: items.length > 0 ? items : [{ iconVariant: 'Rv', label: siteLabel }],
    };
  }

  // For required, return heading with "RV Site Access" and items
  return {
    heading: `${siteLabel} ${intl.formatMessage({ id: 'ListingPage.lodgingAccessTitle' })}`,
    items: items.length > 0 ? items : [{ iconVariant: 'Rv', label: siteLabel }],
  };
};
